import AnimatedCollapsingBox from '@/components/AnimatedHeightBox';
import CheckboxIcon from '@/components/CheckboxIcon';
import {colorStyleMap} from '@/utilities/color-style-map';
import classNames from 'classnames';
import {ComponentChildren} from 'preact';

/**
 * A box for displaying a selectable item.
 * It can take children to display when the item is selected.
 * The `label` prop is the label to display in the box.
 * This may cause overlap with the checkbox, so it is recommended to use this prop only when the label is short.
 * The `color` prop can be used to change the color of the checkbox. See the colors supported by [colorStyleMap].
 */
export default function SelectorItemBox({
  badgeLabel,
  children,
  color = 'primary-blue',
  footer,
  isSelected,
  label,
  onClick,
  showBackgroundColor = false,
  showCheckbox = true,
  dataTestid,
}: SelectorItemBoxProps) {
  const colorStyle =
    colorStyleMap.get(color) ?? colorStyleMap.get('primary-blue')!;

  return (
    <div
      className={classNames(
        'fast-transition relative z-10 h-max rounded-lg border-2',
        {
          'border-neutral-gray': !isSelected,
          [colorStyle.border]: isSelected,
        }
      )}
    >
      <div
        className={classNames('fast-transition absolute inset-0 -z-10', {
          [colorStyle.background]: showBackgroundColor,
          'opacity-10': isSelected,
          'opacity-0': !isSelected,
        })}
      ></div>
      {badgeLabel && (
        <span
          className={classNames(
            'fast-transition block w-full p-1 text-center text-sm font-medium uppercase text-bright-white',
            {
              [colorStyle.background]: isSelected,
              'bg-neutral-gray': !isSelected,
              'cursor-pointer': !!onClick,
            }
          )}
          onClick={onClick}
        >
          {badgeLabel}
        </span>
      )}
      <button
        data-testid={dataTestid}
        data-isSelected={isSelected}
        className={classNames(
          'grid-cols-max-grow relative z-10 grid w-full items-center justify-start p-2 font-medium tracking-normal md:py-3',
          {
            'cursor-default': !onClick,
          }
        )}
        onClick={onClick}
      >
        <div
          className={classNames({
            'mr-1.5 sm:mr-2.5': showCheckbox,
          })}
        >
          {showCheckbox && <CheckboxIcon checked={isSelected} color={color} />}
        </div>
        {label}
      </button>

      <AnimatedCollapsingBox isCollapsed={!isSelected}>
        {children && <div className="relative px-3 pb-3">{children}</div>}
      </AnimatedCollapsingBox>
      {footer}
    </div>
  );
}

export type SelectorItemBoxProps = {
  badgeLabel?: string;
  children?: ComponentChildren;
  color?: string;
  footer?: ComponentChildren;
  isSelected: boolean;
  label: ComponentChildren;
  onClick?: () => void;
  showBackgroundColor?: boolean;
  dataTestid?: string;
  showCheckbox?: boolean;
};
